import { useState } from 'react';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '@/hooks/useApiKeys';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Key, Copy, Check, Trash2, Plus, Loader2, MoreHorizontal, Search } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { PageHeader } from '@/components/ui/page-header';
import { Callout } from '@/components/ui/callout';
import { EmptyState, FilteredEmptyState } from '@/components/ui/empty-state';
import { ListSkeleton } from '@/components/ui/skeletons';
import { DeleteConfirmation } from '@/components/ui/delete-confirmation';
import type { ApiKeyResponse } from '@/lib/api/api-keys';
import { formatDate } from '@/lib/utils';

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
}

function ApiKeysPage() {
  usePageTitle('API Keys');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedPrefixId, setCopiedPrefixId] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyResponse | null>(null);
  const [search, setSearch] = useState('');

  const { data: keys, isLoading } = useApiKeys();
  const createMutation = useCreateApiKey();
  const revokeMutation = useRevokeApiKey();

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    const result = await createMutation.mutateAsync(newKeyName.trim());
    setCreatedKey(result.key);
    setNewKeyName('');
  };

  const handleCopy = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyPrefix = async (key: ApiKeyResponse) => {
    await navigator.clipboard.writeText(key.keyPrefix);
    setCopiedPrefixId(key.id);
    setTimeout(() => setCopiedPrefixId(null), 2000);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    await revokeMutation.mutateAsync(revokeTarget.id);
    setRevokeTarget(null);
  };

  const handleCloseCreateDialog = () => {
    setShowCreateDialog(false);
    setNewKeyName('');
    setCreatedKey(null);
    setCopied(false);
  };

  const allKeys = keys ?? [];
  const query = search.trim().toLowerCase();
  const filteredKeys = allKeys.filter(
    (key) =>
      !query ||
      key.name.toLowerCase().includes(query) ||
      key.keyPrefix.toLowerCase().includes(query),
  );
  const activeCount = filteredKeys.filter((key) => !key.isRevoked).length;

  const emptyState =
    query && allKeys.length > 0 ? (
      <FilteredEmptyState
        entityName="API keys"
        searchValue={search}
        onClear={() => setSearch('')}
      />
    ) : (
      <EmptyState
        icon={Key}
        title="No API keys yet"
        description="Create an API key to access your CRM data programmatically."
        action={{ label: 'Create API Key', onClick: () => setShowCreateDialog(true) }}
      />
    );

  return (
    <div className="space-y-6">
      <PageHeader
        title="API Keys"
        askAi
        description="Manage API keys for programmatic access to your CRM data"
        hideTitleOnMobile={false}
        actions={
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create API Key
          </Button>
        }
      />

      <Card>
        <CardContent className="space-y-3 p-6">
          {isLoading ? (
            <ListSkeleton rows={4} />
          ) : (
            <>
              {/* Desktop rows */}
              <div className="hidden space-y-3 md:block">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search API keys..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-10 pl-9"
                  />
                </div>

                {filteredKeys.length > 0 ? (
                  <div className="overflow-hidden rounded-lg border bg-card">
                    <div className="grid grid-cols-[minmax(0,1fr)_160px_110px_130px_40px] items-center gap-3 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <span>Name</span>
                      <span>Prefix</span>
                      <span>Status</span>
                      <span>Last used</span>
                      <span />
                    </div>
                    {filteredKeys.map((key) => (
                      <div
                        key={key.id}
                        data-api-key-row
                        className={`group/row grid grid-cols-[minmax(0,1fr)_160px_110px_130px_40px] items-center gap-3 border-t px-4 py-3 ${
                          key.isRevoked ? 'opacity-60' : ''
                        }`}
                      >
                        <div className="min-w-0">
                          <p
                            className={`truncate text-sm ${
                              key.isRevoked ? 'text-muted-foreground line-through' : 'font-medium'
                            }`}
                          >
                            {key.name}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            Created {formatRelativeTime(key.createdAt)}
                          </p>
                        </div>
                        <span className="truncate font-mono text-xs">
                          {key.keyPrefix}…
                        </span>
                        {key.isRevoked ? (
                          <Badge variant="destructive">Revoked</Badge>
                        ) : (
                          <Badge variant="secondary">Active</Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(key.lastUsedAt)}
                        </span>
                        <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 opacity-100 md:opacity-0 md:group-hover/row:opacity-100"
                                aria-label={`Actions for ${key.name}`}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => void handleCopyPrefix(key)}>
                                {copiedPrefixId === key.id ? (
                                  <Check className="mr-2 h-3.5 w-3.5 text-primary" />
                                ) : (
                                  <Copy className="mr-2 h-3.5 w-3.5" />
                                )}
                                Copy prefix
                              </DropdownMenuItem>
                              {!key.isRevoked && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    variant="destructive"
                                    onSelect={() => setRevokeTarget(key)}
                                  >
                                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                                    Revoke
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  emptyState
                )}
              </div>

              {/* Mobile card list */}
              <div className="space-y-3 md:hidden">
                <div className="relative">
                  <Input
                    placeholder="Search API keys..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-11"
                  />
                </div>

                {filteredKeys.map((key) => (
                  <div
                    key={key.id}
                    className={`rounded-lg border bg-card p-4 ${key.isRevoked ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p
                          className={`truncate font-medium ${key.isRevoked ? 'line-through' : ''}`}
                        >
                          {key.name}
                        </p>
                        <span className="mt-1 inline-block font-mono text-xs">
                          {key.keyPrefix}…
                        </span>
                      </div>
                      {key.isRevoked ? (
                        <Badge variant="destructive">Revoked</Badge>
                      ) : (
                        <Badge variant="secondary">Active</Badge>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      Created {formatDate(key.createdAt)}
                    </div>
                    {!key.isRevoked && (
                      <div className="mt-3 flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-10 text-destructive"
                          onClick={() => setRevokeTarget(key)}
                          aria-label={`Revoke ${key.name}`}
                        >
                          <Trash2 className="mr-1 h-4 w-4" />
                          Revoke
                        </Button>
                      </div>
                    )}
                  </div>
                ))}

                {filteredKeys.length === 0 && emptyState}
              </div>

              {filteredKeys.length > 0 && (
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {filteredKeys.length} keys · {activeCount} active
                  </span>
                  <span>Full key shown once at creation</span>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={handleCloseCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {createdKey ? 'API Key Created' : 'Create API Key'}
            </DialogTitle>
            <DialogDescription>
              {createdKey
                ? 'Your new API key has been created successfully.'
                : 'Give your API key a descriptive name to remember what it\'s used for.'}
            </DialogDescription>
          </DialogHeader>

          {createdKey ? (
            <div className="space-y-4">
              <Callout variant="warning">
                Make sure to copy your API key now. You won&apos;t be able to see it again!
              </Callout>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-md bg-muted p-3 font-mono text-sm break-all">
                  {createdKey}
                </code>
                <Button variant="outline" size="icon" onClick={handleCopy}>
                  {copied ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Input
                placeholder="e.g., Production Integration"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                autoFocus
              />
            </div>
          )}

          <DialogFooter>
            {createdKey ? (
              <Button onClick={handleCloseCreateDialog}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleCloseCreateDialog}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={!newKeyName.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create'
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmation
        open={!!revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        onConfirm={handleRevoke}
        title="Revoke API key?"
        description={
          revokeTarget ? (
            <>
              Revoking <strong>{revokeTarget.name}</strong> is permanent. Any
              integration using this key will stop working immediately.
            </>
          ) : undefined
        }
        entityType="API key"
        isPending={revokeMutation.isPending}
      />
    </div>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/settings/api-keys')({ component: ApiKeysPage });
